import React, { useState, useRef, useEffect } from "react";
import { MapPin } from "lucide-react";
import { searchAddresses } from "../utils/geocoding";

interface OSMAddressAutocompleteProps {
  onAddressSelect: (address: string, coordinates: [number, number]) => void;
  initialValue?: string;
  placeholder?: string;
  isDarkMode?: boolean;
}

interface SearchResult {
  place_id: string;
  display_name: string;
  formatted_address: string;
  lat: number;
  lon: number;
}

const OSMAddressAutocomplete: React.FC<OSMAddressAutocompleteProps> = ({ onAddressSelect, initialValue = "", placeholder = "Search for an address...", isDarkMode = false }) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [predictions, setPredictions] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimer = useRef<number>();

  // Update input value when initialValue changes
  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  const formatSearchResult = (result: any): SearchResult => {
    // Format address using the same logic as our geocoding utility
    let formattedAddress = result.display_name;

    if (result.address) {
      const parts: string[] = [];

      // Street address: "Road HouseNumber" or just "Road"
      if (result.address.road) {
        if (result.address.house_number) {
          parts.push(`${result.address.road} ${result.address.house_number}`);
        } else {
          parts.push(result.address.road);
        }
      }

      // City with postcode: "Postcode City" or just "City"
      const city = result.address.city || result.address.municipality;
      if (city) {
        if (result.address.postcode) {
          parts.push(`${result.address.postcode} ${city}`);
        } else {
          parts.push(city);
        }
      }

      // Country
      if (result.address.country) {
        parts.push(result.address.country);
      }

      // Use formatted address if we have components
      if (parts.length > 0) {
        formattedAddress = parts.join(", ");
      }
    }

    return {
      place_id: result.place_id,
      display_name: result.display_name,
      formatted_address: formattedAddress,
      lat: parseFloat(result.lat),
      lon: parseFloat(result.lon),
    };
  };

  const searchPlaces = async (query: string) => {
    if (query.length < 3) {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);

    try {
      const results = await searchAddresses(query);
      const formattedResults = results.map(formatSearchResult);

      setPredictions(formattedResults);
      setShowDropdown(formattedResults.length > 0);
    } catch (error) {
      console.error("Error searching addresses:", error);
      setPredictions([]);
      setShowDropdown(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);

    // Debounce the search to avoid too many API calls
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      searchPlaces(value);
    }, 300);
  };

  const handlePlaceSelect = (result: SearchResult) => {
    setInputValue(result.formatted_address);
    setShowDropdown(false);
    onAddressSelect(result.formatted_address, [result.lat, result.lon]);
  };

  const handleBlur = () => {
    // Delay hiding dropdown to allow click events
    setTimeout(() => setShowDropdown(false), 200);
  };

  return (
    <div className="relative">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => inputValue.length >= 3 && predictions.length > 0 && setShowDropdown(true)}
          onBlur={handleBlur}
          placeholder={placeholder}
          className={`w-full px-3 py-2 pl-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? "border-gray-600 bg-gray-700 text-white" : "border-gray-300 bg-white text-gray-900"}`}
        />
        <MapPin size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          </div>
        )}
      </div>

      {showDropdown && predictions.length > 0 && (
        <div className={`absolute z-50 w-full mt-1 border rounded-md shadow-lg max-h-60 overflow-auto ${isDarkMode ? "bg-gray-800 border-gray-600" : "bg-white border-gray-300"}`}>
          {predictions.map((result) => (
            <button
              key={result.place_id}
              type="button"
              onClick={() => handlePlaceSelect(result)}
              className={`w-full px-4 py-3 text-left border-b last:border-b-0 focus:outline-none ${
                isDarkMode ? "hover:bg-gray-700 focus:bg-gray-700 border-gray-600 text-white" : "hover:bg-gray-100 focus:bg-gray-100 border-gray-200 text-gray-900"
              }`}
            >
              <div className="font-medium text-sm">{result.formatted_address}</div>
              {result.formatted_address !== result.display_name && <div className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>{result.display_name}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default OSMAddressAutocomplete;
